// A week of the VALS court diary, as it is stored and as it travels.
//
// The diary used to keep its week in the browser. On portal.completelawsupport.com
// clientStorage resolves to sessionStorage, so every edit lived in one tab and went when
// that tab closed: a funding status ticked on Monday was not there on Tuesday, and nobody
// else could see it at all. A week is now saved through /api/portal-drafts like every other
// tool's work, under form type "vals_diary" and the VALS firm, so it outlives the tab and
// the same week opens from the VALS page and the admin page. EAS's diary comes in the same
// format and is kept the same way, under EAS (src/lib/diaryFirms.js).
//
// What is stored is the same archive the "Save a copy" button writes to disk, so a week held
// in the portal and a week saved to a file are interchangeable in both directions. Weeks
// saved to a file before any of this existed still open.

import { valsDiaryDaySortValue } from "./valsDiary.js";

export const VALS_DIARY_FORM_TYPE = "vals_diary";
export const VALS_DIARY_BRAND = "vals";
export const VALS_WEEK_FORMAT = "cls-vals-weekly-diary";
export const VALS_WEEK_FORMAT_VERSION = 1;

// api/portal-drafts.js refuses a form_data over this size. Checked here so a week that is
// too big is named as such before a request goes out, rather than coming back as the
// server's own wording about drafts.
export const MAX_WEEK_BYTES = 512 * 1024;

export function weekArchive({ weekName, sourceFileName, entries, savedAt } = {}) {
  return {
    format: VALS_WEEK_FORMAT,
    version: VALS_WEEK_FORMAT_VERSION,
    weekName: String(weekName || "").trim() || "VALS Diary",
    sourceFileName: String(sourceFileName || ""),
    savedAt: savedAt || new Date().toISOString(),
    entries: Array.isArray(entries) ? entries : [],
  };
}

export function weekTooLarge(archive) {
  return JSON.stringify(archive ?? {}).length > MAX_WEEK_BYTES;
}

// Reading a week back. Tolerant, because a week can arrive as this archive, as a bare list
// of entries from an early save, or as text.
export function readWeekArchive(value) {
  let source = value;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      return null;
    }
  }
  if (Array.isArray(source)) {
    return source.length ? { weekName: "", sourceFileName: "", savedAt: "", entries: source } : null;
  }
  if (!source || typeof source !== "object") return null;
  if (source.format && source.format !== VALS_WEEK_FORMAT) return null;
  if (!Array.isArray(source.entries)) return null;
  return {
    weekName: String(source.weekName || "").trim(),
    sourceFileName: String(source.sourceFileName || ""),
    savedAt: source.savedAt || "",
    entries: source.entries,
  };
}

// A week opened from a file the person chose. Stricter than the above: an unrelated JSON
// file should be refused by name rather than loaded as an empty diary.
export function readWeekFile(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("This is not a saved weekly diary file.");
  }
  if (parsed?.format !== VALS_WEEK_FORMAT || !Array.isArray(parsed.entries)) {
    throw new Error("This is not a saved weekly diary file.");
  }
  return readWeekArchive(parsed);
}

const weekTime = (row) => {
  const value = row?.updated_at || row?.created_at || row?.form_data?.savedAt || "";
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

// Which week the diary opens on: the one edited most recently, so the page comes back where
// it was left rather than empty.
export function pickLatestWeek(rows = []) {
  const weeks = (Array.isArray(rows) ? rows : []).filter((row) => readWeekArchive(row?.form_data));
  if (!weeks.length) return null;
  return weeks.reduce((latest, row) => (weekTime(row) > weekTime(latest) ? row : latest));
}

// What the list of saved weeks shows. The entry count is the honest way to tell two weeks
// apart when the names are similar.
export function weekSummaries(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const archive = readWeekArchive(row?.form_data);
      if (!archive) return null;
      return {
        id: row.id,
        weekName: archive.weekName || row.draft_name || "VALS Diary",
        entryCount: archive.entries.length,
        savedAt: row.updated_at || archive.savedAt || "",
        savedBy: row.saved_by || "",
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
}

// What a week is called: the diary PDF it came from, or the days it covers. Used for the
// stored week's name and for the file name of a copy saved to disk, so the two agree.
export function weekNameFor(entries = [], sourceFileName = "", firmName = "VALS") {
  const fromFile = String(sourceFileName || "").trim();
  if (fromFile) return fromFile.replace(/\.pdf$/i, "");
  const days = Array.from(new Set((Array.isArray(entries) ? entries : []).map((entry) => entry?.date).filter(Boolean)))
    .sort((a, b) => valsDiaryDaySortValue(a) - valsDiaryDaySortValue(b));
  if (days.length === 1) return `${firmName} Diary - ${days[0]}`;
  if (days.length > 1) return `${firmName} Diary - ${days[0]} to ${days[days.length - 1]}`;
  return `${firmName} Diary - ${new Date().toLocaleDateString("en-AU")}`;
}
